/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: intro.section.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { type FunctionComponent, type ReactNode, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import Statics from '@/constants/statics';
import App from '@/constants/app';
import BasicComponents from '@/shared/basic.components';
import Assets from '@/constants/assets';
import Logo from '@/shared/logo';
/**************************************************************************************************************************
 TYPES / GLOBAL DEFINITIONS
***************************************************************************************************************************/
interface AnimatedQuoteProps {
  title: ReactNode;
  subtitle: ReactNode;
  desc: string;
}

/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const AnimatedQuote: FunctionComponent<AnimatedQuoteProps> = ({
  title,
  subtitle,
  desc
}: AnimatedQuoteProps) => {
  return (
    <div className="w-full max-w-screen-md mx-auto flex flex-col items-center justify-center gap-4">
      <motion.h2
        className="text-3xl md:text-4xl lg:text-5xl text-white font-montserrat font-extrabold flex flex-wrap items-center justify-center gap-4"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        transition={{
          duration: 0.5,
          delay: 0.5
        }}
      >
        {title}
      </motion.h2>
      <motion.h3
        className="text-3xl md:text-4xl lg:text-5xl text-white font-montserrat font-extrabold flex flex-wrap items-center justify-center gap-4"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{
          delay: 0.6,
          duration: 0.5
        }}
      >
        {subtitle}
      </motion.h3>
      <motion.p
        className="text-md lg:text-lg text-center text-white font-roboto font-medium tracking-[2px] mt-5 lg:mt-10"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{
          delay: 1.1,
          duration: 0.75
        }}
      >
        {desc}
      </motion.p>
    </div>
  );
};

const Home: FunctionComponent = () => {
  const { t } = useTranslation('home-page');

  const quotes = [
    <AnimatedQuote
      key={0}
      title={
        <Trans key="home-page.intro.quotes.one.first">
          Forging dreams into{' '}
          <span className="text-[45px] lg:text-[60px] text-theme-900">Steel</span>
        </Trans>
      }
      subtitle={
        <Trans key="home-page.intro.quotes.one.second">
          One <span className="text-[45px] lg:text-[60px] text-theme-900">Detail</span> at a time
        </Trans>
      }
      desc={t('intro.quotes.one.desc')}
    />,
    <AnimatedQuote
      key={1}
      title={
        <Trans key="home-page.intro.quotes.two.first">
          <span className="text-[45px] lg:text-[60px] text-theme-900">Precision</span> meets
          strength
        </Trans>
      }
      subtitle={
        <Trans key="home-page.intro.quotes.two.second">
          Steel detailing at its{' '}
          <span className="text-[45px] lg:text-[60px] text-theme-900">Finest</span>
        </Trans>
      }
      desc={t('intro.quotes.two.desc')}
    />,
    <AnimatedQuote
      key={2}
      title={
        <Trans key="home-page.intro.quotes.three.first">
          In World of <span className="text-[45px] lg:text-[60px] text-theme-900">Steel</span>
        </Trans>
      }
      subtitle={
        <Trans key="home-page.intro.quotes.three.second">
          Every <span className="text-[45px] lg:text-[60px] text-theme-900">Detail</span> Counts
        </Trans>
      }
      desc={t('intro.quotes.three.desc')}
    />
  ];

  const [currentBgIndex, setCurrentBgIndex] = useState<number>(0);
  const [currentDescIndex, setCurrentDescIndex] = useState<number>(0);

  const getNextIndex = () => {
    setCurrentBgIndex(currentBgIndex === Statics.IntroBgItems.length - 1 ? 0 : currentBgIndex + 1);
    setCurrentDescIndex(currentDescIndex === quotes.length - 1 ? 0 : currentDescIndex + 1);
  };

  useEffect(() => {
    const slideInterval = setInterval(getNextIndex, App.SlideInterval);

    return () => {
      clearInterval(slideInterval);
    };
  });

  return (
    <section id="home">
      <div className="w-full grid grid-cols-1 lg:grid-cols-3">
        <motion.div
          className="relative col-span-1 h-fit lg:h-[calc(100vh-96.5px)] bg-black flex flex-col items-center justify-center gap-10 py-10 px-10 shadow-md drop-shadow-md"
          initial="initial"
          whileInView="entry"
          viewport={{ once: true }}
          variants={{
            initial: {
              x: -200,
              opacity: 0
            },
            entry: {
              x: 0,
              opacity: 1
            }
          }}
          transition={{
            duration: 0.5
          }}
        >
          <motion.div
            className="absolute top-0 left-0 w-full h-fit flex flex-col items-center justify-center"
            initial="initial"
            whileInView="entry"
            viewport={{ once: true }}
            variants={{
              initial: {
                opacity: 0
              },
              entry: {
                opacity: 1
              }
            }}
            transition={{
              duration: 0.5,
              delay: 0.5
            }}
          >
            <img
              src="/assets/images/intro/intro-3.png"
              alt="intro-2"
              className="w-full object-cover"
            />
          </motion.div>
          <motion.div
            className="flex flex-col items-center justify-center border-[3px] border-theme-900 rounded-2xl px-5 py-5 mt-10"
            initial="initial"
            whileInView="entry"
            viewport={{ once: true }}
            variants={{
              initial: {
                y: -100,
                opacity: 0
              },
              entry: {
                y: 0,
                opacity: 1
              }
            }}
            transition={{
              duration: 0.5,
              delay: 0.5
            }}
          >
            <Logo vertical logoSize={200} titleSize={40} tagLineSize={18} />
          </motion.div>
          <motion.h3
            className="w-full h-[120px] max-w-screen-md text-lg text-center text-white font-inter font-medium"
            initial="initial"
            whileInView="entry"
            viewport={{ once: true }}
            variants={{
              initial: {
                x: 100,
                opacity: 0
              },
              entry: {
                x: 0,
                opacity: 1
              }
            }}
            transition={{
              duration: 0.5,
              delay: 0.75
            }}
          >
            Precision in every line <br /> Excellence in every steel detail.
          </motion.h3>
          <motion.div
            className="absolute bottom-0 left-0 w-full h-fit flex flex-col items-center justify-center"
            initial="initial"
            whileInView="entry"
            viewport={{ once: true }}
            variants={{
              initial: {
                opacity: 0
              },
              entry: {
                opacity: 1
              }
            }}
            transition={{
              duration: 0.5,
              delay: 0.5
            }}
          >
            <img
              src="/assets/images/intro/intro-2.png"
              alt="intro-2"
              className="w-full object-cover"
            />
          </motion.div>
        </motion.div>
        <motion.div
          className="col-span-2 relative w-full h-[calc(100vh-96.5px)] overflow-hidden"
          initial="initial"
          whileInView="entry"
          viewport={{ once: true }}
          variants={{
            initial: {
              x: 200,
              opacity: 0
            },
            entry: {
              x: 0,
              opacity: 1
            }
          }}
          transition={{
            duration: 0.5
          }}
        >
          <div className="bg-image w-full h-full">
            <img
              className="slowzoom w-full h-full object-cover"
              src={Statics.IntroBgItems[currentBgIndex].src}
              alt="intro"
            />
          </div>
          <div className="intro-text px-5">
            <AnimatePresence mode="wait">{quotes[currentDescIndex]}</AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Projects: FunctionComponent = () => {
  const { t } = useTranslation('projects');

  return (
    <BasicComponents.PageIntro
      asset={Assets.Images.INTRO_BG}
      title={t('intro.title')}
      desc={t('intro.desc')}
    />
  );
};

const Contact: FunctionComponent = () => {
  const { t } = useTranslation('contact-us');

  return (
    <BasicComponents.PageIntro
      asset={Assets.Images.INTRO_BG}
      title={t('intro.title')}
      desc={t('intro.desc')}
    />
  );
};

const Careers: FunctionComponent = () => {
  const { t } = useTranslation('careers');

  return (
    <BasicComponents.PageIntro
      asset={Assets.Images.INTRO_BG}
      title={t('intro.title')}
      desc={t('intro.desc')}
    />
  );
};

const Intros = {
  Home,
  Careers,
  Contact,
  Projects
};

export default Intros;
